import { atom, SetStateAction } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { atomWithReset, RESET } from 'jotai/utils';
import { z } from 'zod';

// Object of JS
const locationsSchema = z.object({
  account: z.string().nullable(),
  countries: z.array(z.string()),
  productType: z.array(z.string()),
  activity: z.number().nullable(),
  view: z.string(),
  selectedId: z.string().nullable(),
});

// Object of URLParams
const paramsSchema = z
  .object({
    account: z.string(),
    countries: z.string(),
    productType: z.string(),
    activity: z.string(),
    view: z.string(),
    selectedId: z.string(),
  })
  .partial();

const locationsCodec = z.codec(locationsSchema, paramsSchema, {
  /** Decode Locations object to Search params */
  decode: (input) => {
    return {
      ...(input.account && { account: input.account }),
      ...(input.view && { view: input.view }),
      ...(input.selectedId && { selectedId: input.selectedId.toString() }),
      ...(input.countries.length > 0 && { countries: input.countries.join(',') }),
      ...(input.productType.length > 0 && { productType: input.productType.join(',') }),
      ...(input.activity && { activity: input.activity.toString() }),
    };
  },
  /** Encode Search params to Locations object */
  encode: (output) => {
    return {
      view: output.view ?? 'product',
      account: output.account ?? null,
      selectedId: output.selectedId ?? null,
      countries: output.countries ? output.countries.split(',') : [],
      productType: output.productType ? output.productType.split(',') : [],
      activity: output.activity ? Number.parseInt(output.activity) : null,
    };
  },
});

type LocationsParams = z.infer<typeof locationsSchema>;

const INITIAL_LOCATIONS_PARAMS: LocationsParams = {
  account: null,
  countries: [],
  productType: [],
  activity: null,
  view: 'product',
  selectedId: null,
};

const primitiveLocationsAtom = atomWithReset(INITIAL_LOCATIONS_PARAMS);

export const locationsAtom = atom(
  (get) => get(primitiveLocationsAtom),
  (_get, set, newValue: SetStateAction<LocationsParams | typeof RESET>) => {
    if (newValue === RESET) {
      return set(primitiveLocationsAtom, INITIAL_LOCATIONS_PARAMS);
    }

    return set(primitiveLocationsAtom, newValue);
  },
);

export const locationsAccountAtom = focusAtom(locationsAtom, (o) => o.prop('account'));
export const locationsCountriesAtom = focusAtom(locationsAtom, (o) => o.prop('countries'));
export const locationsProductTypesAtom = focusAtom(locationsAtom, (o) => o.prop('productType'));
export const locationsActivityAtom = focusAtom(locationsAtom, (o) => o.prop('activity'));
export const locationsViewAtom = focusAtom(locationsAtom, (o) => o.prop('view'));
export const locationsSelectedFeatureIdAtom = focusAtom(locationsAtom, (o) => o.prop('selectedId'));

export const locationsUrlParamsAtom = atom(
  (get) => {
    // convert the locations object to URLSearchParams
    const filters = get(locationsAtom);

    const filtersForParams = locationsCodec.decode(filters);
    const searchParams = new URLSearchParams(filtersForParams);

    return searchParams;
  },
  (_, set, urlSearchParams: URLSearchParams) => {
    // take some urlsearchparams and turn it into the object
    const filters = Object.fromEntries(urlSearchParams);
    const obj = locationsCodec.encode(filters);

    set(locationsAtom, obj);
  },
);
